import classNames from 'classnames';
import React, { forwardRef } from 'react';
import PasswordStrengthBar from '../../PasswordStrengthBar/PasswordStrengthBar.js';
import styles from './TextInput.scss.js';
import { fontWeightOptions } from '../../../../helpers/enums/themes/fontStyleOptions.js';
import SuccessIcon from '../../IconsDeprecated/SuccessIcon.js';
import WarningIcon from '../../IconsDeprecated/WarningIcon.js';
import ErrorIcon from '../../IconsDeprecated/ErrorIcon.js';
import { sizeOptions } from '../../../../helpers/enums/themes/sizeOptions.js';

let TextInputMessageType = /*#__PURE__*/function (TextInputMessageType) {
  TextInputMessageType["Success"] = "Success";
  TextInputMessageType["Warning"] = "Warning";
  TextInputMessageType["Error"] = "Error";
  return TextInputMessageType;
}({});
let TextInputTheme = /*#__PURE__*/function (TextInputTheme) {
  TextInputTheme["default"] = "default";
  TextInputTheme["darker"] = "darker";
  return TextInputTheme;
}({});
const inputMessageTypeClassnamesMap = {
  Success: styles['container--success'],
  Warning: styles['container--warning'],
  Error: styles['container--error']
};
const inputIconsMap = {
  Success: /*#__PURE__*/React.createElement(SuccessIcon, null),
  Warning: /*#__PURE__*/React.createElement(WarningIcon, null),
  Error: /*#__PURE__*/React.createElement(ErrorIcon, null)
};

// This input as made simple for it to be easy to extend/use in the future
// @TODO placeholder's alignment is not consistent across browsers, that should be fixed
const TextInput = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    type = 'text',
    value,
    onChange,
    placeholder,
    label,
    'aria-label': ariaLabel,
    message,
    backgroundColor = 'transparent',
    fontWeight = fontWeightOptions.light,
    fontSize = sizeOptions.medium,
    hideMessageSpace = false,
    heightSize = sizeOptions.medium,
    readonly = false,
    isMultiline = false,
    resizeMultiline = 'none',
    multilineMaxHeight = '280px',
    maxLength,
    showMaxLengthChars = true,
    showPasswordStrength = false,
    onBlur,
    hasAutoFocus = false,
    isRequiredLabel = false,
    iconPrefix,
    isSmartStyle = false,
    inputId = '',
    labelFontSize = 'size-default',
    theme = TextInputTheme.default
  } = _ref;
  let inputStatusClassName = null;
  let inputIcon = null;
  if (message) {
    inputStatusClassName = inputMessageTypeClassnamesMap[message.type];
    inputIcon = inputIconsMap[message.type];
  }
  const hasMaxLength = !!maxLength && value;
  const remainingCharacters = hasMaxLength ? maxLength - value.toString().length : '';
  const hasPassword = !!value?.toString().length;
  const hasSideContent = !!message && !maxLength || !message && !!maxLength;
  const hasExtraSideContent = !!maxLength && !!message;
  const getResizeStyles = () => {
    if (!isMultiline || resizeMultiline === 'none') return {};
    return {
      resize: resizeMultiline,
      overflow: 'auto',
      maxHeight: multilineMaxHeight
    };
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.container, inputStatusClassName)
  }, (label || ariaLabel) && /*#__PURE__*/React.createElement("label", {
    className: classNames(styles.label, styles[`label--theme-${theme}`], {
      [styles[`label--${labelFontSize}`]]: labelFontSize,
      [styles['label--hide']]: !label
    }),
    htmlFor: inputId,
    "aria-label": ariaLabel
  }, label, isRequiredLabel && /*#__PURE__*/React.createElement("span", {
    className: styles.label__required
  }, "*")), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['input-container'], styles[`input-container--theme-${theme}`], {
      [styles[`input-container--background-color-${backgroundColor}`]]: true,
      [styles[`input-container--height-${heightSize}`]]: !isMultiline,
      [styles['input-container--smart']]: isSmartStyle,
      [styles['input-container--without-right-space']]: isMultiline
    }),
    style: getResizeStyles()
  }, !!iconPrefix && /*#__PURE__*/React.createElement("div", {
    className: styles['input-container__icon-prefix']
  }, iconPrefix), !isMultiline ? /*#__PURE__*/React.createElement("input", {
    type: type,
    readOnly: readonly,
    className: classNames(styles[`input-container-input`], styles[`input-container-input--${fontWeight}`], styles[`input-container-input--${fontSize}`], {
      [styles['input-container-input--with-side-content']]: hasSideContent,
      [styles['input-container-input--with-extra-side-content']]: hasExtraSideContent
    }),
    value: value || '',
    placeholder: placeholder,
    maxLength: maxLength,
    ref: ref,
    onChange: e => onChange(e.target.value),
    onBlur: onBlur,
    autoFocus: hasAutoFocus /* eslint-disable-line jsx-a11y/no-autofocus */,
    id: inputId,
    required: isRequiredLabel,
    "aria-describedby": `${inputId}-message`
  }) : /*#__PURE__*/React.createElement("textarea", {
    className: classNames(styles[`input-container-input`], styles[`input-container-input--${fontWeight}`], styles[`input-container-input--${fontSize}`], styles[`input-container-input--textarea`], {
      [styles['input-container-input--textarea--with-side-content']]: hasSideContent,
      [styles['input-container-input--textarea--with-extra-side-content']]: hasExtraSideContent
    }),
    style: {
      height: resizeMultiline !== 'none' ? '100%' : undefined,
      minHeight: resizeMultiline !== 'none' ? '68px' : undefined
    },
    readOnly: readonly,
    placeholder: placeholder,
    maxLength: maxLength,
    ref: ref,
    onChange: e => onChange(e.target.value),
    onBlur: onBlur,
    value: value || '',
    "aria-describedby": `${inputId}-message`
  }), /*#__PURE__*/React.createElement("p", {
    className: classNames(styles[`input-container_counter`], {
      [styles[`input-container_counter--with-side-content`]]: !!message
    })
  }, showMaxLengthChars && remainingCharacters), !!inputIcon && /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['input-container-icon'], {
      [styles['input-container-icon--with-side-content']]: !!message && isMultiline
    })
  }, inputIcon)), showPasswordStrength && hasPassword ? /*#__PURE__*/React.createElement(PasswordStrengthBar, {
    password: value.toString()
  }) : null, (!hideMessageSpace || message) && /*#__PURE__*/React.createElement("span", {
    id: `${inputId}-message`,
    "aria-live": "polite",
    className: styles.message
  }, message?.message));
});
var TextInput$1 = TextInput;

export { TextInputMessageType, TextInputTheme, TextInput$1 as default };
//# sourceMappingURL=TextInput.js.map
